import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { ProductCard } from "@/components/ProductCard";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { Loader } from "@/components/States";
import { Heart, Leaf, ShieldCheck, Truck, ArrowRight } from "lucide-react";

const heroImg =
  "https://images.pexels.com/photos/34357862/pexels-photo-34357862.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Home() {
  const { settings } = useSettings();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([axios.get(`${API}/products`), axios.get(`${API}/categories`)])
      .then(([p, c]) => {
        setProducts(p.data);
        setCategories(c.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const featured = products.slice(0, 4);
  const bestsellers = products.filter((p) => p.bestseller).slice(0, 4);

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <section className="relative overflow-hidden bg-cream">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-16 sm:px-6 md:grid-cols-2 lg:px-8 lg:py-24">
          <div>
            <p className="font-accent text-2xl text-brand">{settings.tagline || "Homemade • Made With Love"}</p>
            <h1 className="mt-3 font-heading text-4xl font-semibold leading-tight text-brand-dark sm:text-5xl lg:text-6xl">
              Handcrafted treats,<br />
              <span className="text-brand">made with love</span>
            </h1>
            <p className="mt-5 max-w-md text-base leading-relaxed text-brand-muted">
              From decadent chocolates and fudgy brownies to wholesome laddus and authentic pickles — every bite from{" "}
              {settings.business_name || "Taanya Homefoods"} is pure, fresh & trusted.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/shop"
                data-testid="hero-shop-btn"
                className="inline-flex items-center gap-2 rounded-full bg-brand px-8 py-3 font-medium text-white shadow-sm transition-transform hover:-translate-y-0.5 hover:bg-brand-hover"
              >
                Shop Now <ArrowRight className="h-4 w-4" />
              </Link>
              <WhatsAppButton
                label="Enquire on WhatsApp"
                variant="outline"
                testId="hero-whatsapp-btn"
                message={`Hi ${settings.business_name || "Taanya Homefoods"}, I'd like to know more about your products.`}
              />
            </div>
          </div>
          <div className="relative">
            <img
              src={heroImg}
              alt="Homemade treats"
              className="max-h-[70vh] w-full rounded-3xl object-cover shadow-xl"
            />
            <div className="absolute -bottom-5 -left-5 hidden rounded-2xl border border-brand-line bg-white px-5 py-4 shadow-lg sm:block">
              <p className="font-heading text-2xl text-brand">17+ Treats</p>
              <p className="text-xs uppercase tracking-widest text-brand-muted">Freshly handmade</p>
            </div>
          </div>
        </div>
      </section>

      {loading ? (
        <Loader />
      ) : (
        <>
          {/* Categories */}
          <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <div className="mb-10 text-center">
              <p className="text-sm uppercase tracking-[0.2em] text-brand-gold">Browse</p>
              <h2 className="mt-2 font-heading text-3xl text-brand-dark sm:text-4xl">Our Categories</h2>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
              {categories.map((c) => (
                <Link
                  key={c.id}
                  to={`/shop?category=${c.slug}`}
                  data-testid={`home-category-${c.slug}`}
                  className="group relative overflow-hidden rounded-2xl border border-brand-line"
                >
                  <img
                    src={c.image}
                    alt={c.name}
                    className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/80 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-center">
                    <span className="text-lg">{c.emoji}</span>
                    <p className="font-heading text-lg text-white">{c.name}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* Featured */}
          <section className="bg-white py-16">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="mb-10 flex items-end justify-between">
                <div>
                  <p className="text-sm uppercase tracking-[0.2em] text-brand-gold">Handpicked</p>
                  <h2 className="mt-2 font-heading text-3xl text-brand-dark sm:text-4xl">Featured Products</h2>
                </div>
                <Link to="/shop" className="hidden items-center gap-1 text-sm font-medium text-brand hover:underline sm:flex">
                  View all <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {featured.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            </div>
          </section>

          {/* Bestseller */}
          {bestsellers.length > 0 && (
            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
              <div className="mb-10 text-center">
                <p className="text-sm uppercase tracking-[0.2em] text-brand-gold">Loved by all</p>
                <h2 className="mt-2 font-heading text-3xl text-brand-dark sm:text-4xl">Bestsellers</h2>
              </div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {bestsellers.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            </section>
          )}

          {/* Why choose us */}
          <section className="bg-brand-gold-light/40 py-16">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="mb-12 text-center">
                <p className="text-sm uppercase tracking-[0.2em] text-brand-gold">Pure • Fresh • Trusted</p>
                <h2 className="mt-2 font-heading text-3xl text-brand-dark sm:text-4xl">Why Choose Us</h2>
              </div>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
                {[
                  { icon: Heart, title: "Made With Love", text: "Every batch handcrafted in a home kitchen." },
                  { icon: Leaf, title: "Pure & Fresh", text: "Quality ingredients, no shortcuts." },
                  { icon: ShieldCheck, title: "Trusted Quality", text: "Loved by families across the city." },
                  { icon: Truck, title: "Easy Ordering", text: "Order online or on WhatsApp in minutes." },
                ].map((f) => (
                  <div key={f.title} className="rounded-2xl border border-brand-line bg-white p-6 text-center">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-brand/10 text-brand">
                      <f.icon className="h-6 w-6" />
                    </div>
                    <h3 className="font-heading text-xl text-brand-dark">{f.title}</h3>
                    <p className="mt-2 text-sm text-brand-muted">{f.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8">
            <div className="rounded-3xl bg-brand-green px-8 py-14 text-center text-cream">
              <h2 className="font-heading text-3xl text-white sm:text-4xl">Craving something homemade?</h2>
              <p className="mx-auto mt-3 max-w-md text-cream/85">
                Explore our full range of handmade delicacies and place your order today.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Link
                  to="/shop"
                  data-testid="cta-shop-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-brand-gold px-8 py-3 font-medium text-brand-dark transition-transform hover:-translate-y-0.5"
                >
                  Start Shopping <ArrowRight className="h-4 w-4" />
                </Link>
                <WhatsAppButton testId="cta-whatsapp-btn" message={`Hi ${settings.business_name || "Taanya Homefoods"}, I'd like to place an order.`} />
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
